import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Option } from '@ptg-shared/controls/select/select.component';
import { deepClone } from '@ptg-shared/utils/common.util';
import { EmployerFilter } from '@ptg-employer/models/employer-filter.model';

import * as StatusActions from '../../store/actions/status.actions';
import {
  AgePropertyName,
  FilterPropertyType,
  INPUT_TYPE,
  Operator,
  OPERATOR_BY_TYPE,
  OPERATOR_LABEL,
  SystemProperties,
} from '../../constance/member-list.const';
import { InputType } from '../../constance/metadataPropertyType.const';
import {
  FilterByPropertyConfigEntity,
  FilterInputType,
  MemberFilterEntity,
  MemberStatus,
  PropertyItem,
} from '../../types/models';
import * as fromMember from '../../store/reducers';
import { MemberListActions } from '../../store/actions';
import { OptionValue } from '@ptg-shared/types/models';
import { SCREEN_ID } from '@ptg-member/constance/lookupTable.const';
import { EntityPropertyType } from '@ptg-entity-management/types/enums/entity-property.enum';
import { VERTICAL_LINE_SEPARATOR } from '@ptg-shared/constance/common.const';
import { LayoutService } from '@ptg-shared/services/layout.service';
import { LookupTableType } from '@ptg-shared/types/enums';
import { AddressField } from '@ptg-member/features/calculation/types/enums';

@Component({
  selector: 'ptg-add-member-filters',
  templateUrl: './add-member-filters.component.html',
  styleUrls: ['./add-member-filters.component.scss']
})
export class AddMemberFiltersComponent implements OnInit, OnDestroy {
  unsubscribe$ = new Subject<void>();
  cardSeclectionOptions: Option[] = [];
  listSectionProperty: any[] = [];
  filterByOptions: Option[] = [];
  filterOperators: Option[] = [];
  filterFormGroup: FormGroup = this.fb.group({
    cardId: this.fb.control(null),
    filterBy: this.fb.control('', [Validators.required]),
    operator: this.fb.control('', [Validators.required]),
    valueFrom: this.fb.control('', [Validators.required]),
    valueTo: this.fb.control('', [this.validateBetweenValue()]),
  });
  currentFilters: MemberFilterEntity[] = [];
  placeholder = {
    from: 'Value',
    to: 'Value To',
  };
  TEXT_BOX_INPUT_TYPE = InputType;
  inputType: FilterInputType = INPUT_TYPE[FilterPropertyType.Text];
  FIXED_PROPERTIES: Record<string, string> = {
    MemberiId: 'Member Id',
    Registered: 'Registered',
    Locked: 'Locked',
    last4Ssn: 'SSN',
    PaperlessOptedIn: 'Paperless Opted-In',
    SMSOptedIn: 'SMS Opted-In',
    FixedMember: 'FixedMember',
    FixedParticipant: 'FixedParticipant',
    FixedPerson: 'FixedPerson'
  };

  booleanOptions: Option[] = [
    { displayValue: 'Yes', value: true },
    { displayValue: 'No', value: false },
  ];
  inputConfig: {
    suffix: string;
    isPositive: boolean;
    isDecimal: boolean;
    allowZero: boolean;
  } = {
    suffix: '',
    isPositive: true,
    isDecimal: true,
    allowZero: true,
  };
  cardId: string = '';
  statusOptions: Option[] = [];
  eventOptions: Option[] = [];
  eventOperatorOptions: Option[] = [];
  dateOperatorOptions: Option[] = [];
  valueOptions: Option[] = [];
  isMultipleLineOption: boolean = false;
  propertyConfigs: any = [];
  listCalculationAndAggregation: number[] = [EntityPropertyType.Calculation, EntityPropertyType.Aggregation];
  listDefaultData!: any[]
  constructor(
    private fb: FormBuilder,
    private memberStore: Store<fromMember.MemberState>,
    public dialogRef: MatDialogRef<AddMemberFiltersComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      currentFilters: MemberFilterEntity[],
      entityId: string
    },
    private layoutService: LayoutService,
  ) {}

  ngOnInit(): void {
    this.getPropertyOfParticipant();
    this.getCardSelection();
    this.memberStore.dispatch(
      MemberListActions.getFilterByPropertyListRequest()
    );
    this.memberStore.dispatch(
      StatusActions.getStatus({ query: { SortNames: 'Name', SortType: 0 } })
    );

    this.memberStore
      .select(fromMember.selectMemberStatusList)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((statusList) => {
        this.statusOptions = (deepClone(statusList) as MemberStatus[]).map(
          (status) => {
            return {
              value: status.id,
              displayValue: status.name,
            };
          }
        );
      });

    this.memberStore
      .select(fromMember.selectStatusDetail)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((statusDetailState) => {
        if (
          this.filterFormGroup.value.filterBy.type !==
            FilterPropertyType.Status ||
          !this.filterFormGroup.value.valueFrom
        ) {
          this.eventOptions = [];
          return;
        }
        this.eventOptions = (
          statusDetailState?.statusDetail?.memberEvents || []
        ).map((event) => {
          return {
            value: event.id,
            displayValue: event.name,
          };
        });
      });
    this.updateValidityFormGroup();

    this.memberStore
    .pipe(
      select(fromMember.selectPropertyOfParticipants),
      takeUntil(this.unsubscribe$)
    )
    .subscribe((res: any) => {
      if(res?.propertyList?.propertyOfParticipants.length) {
        const propertyOfParticipants = deepClone(res?.propertyList?.propertyOfParticipants);
        this.listDefaultData = propertyOfParticipants;
        this.mapSectionPropertyData(propertyOfParticipants);
        if (this.data?.currentFilters) {
          this.currentFilters = deepClone(this.data.currentFilters);
          this.currentFilters.forEach(value => {
            if(value.entityReferencePropertyId) {
              value.id = `${value?.id?.split(VERTICAL_LINE_SEPARATOR)[0]}|${value.entityReferencePropertyId}`;
            }
          });
          this.getFilterByOptions();
        }
      }
    });

    this.memberStore
      .pipe(
        select(fromMember.selectCardSelection),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((res: any) => {
        this.cardSeclectionOptions = res.cards?.filter((element: any) => element.totalProperties !== 0).map((item: any) => ({
          value: item?.id,
          displayValue: item?.cardName
        } as Option)).sort((a: any, b: any) => a.displayValue.localeCompare(b.displayValue));
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getPropertyOfParticipant() {
    const query = {
      isIncludePropertyEntityReference: false,
      isIncludePropertyOfList: false,
      isUseFilter: true,
      screenId: SCREEN_ID.ParticipantList,
    };
    this.memberStore.dispatch(MemberListActions.getPropertyOfParticipantsRequestRequest({ entityId: this.data.entityId, query }));
  }

  getCardSelection() {
    const query = {
      sortField: 'cardName',
      sortType: 0,
      filterByEntityIds: this.data.entityId,
      includeSystemCard: false
    };
    this.memberStore.dispatch(MemberListActions.getCardSelectionRequest({ query }));
  }
  private isStatus(item:FilterByPropertyConfigEntity){
    return item.type === FilterPropertyType.System && item.propertyKey === "Status|Event"
  }
  mapSectionPropertyData(listSectionProperty: FilterByPropertyConfigEntity[]) {
    let listSP = listSectionProperty.filter(item => !this.isStatus(item)).map((item) => {
      return {
        ...item,
        id: item?.entityPropertyId ? item.entityPropertyId : item?.id
      }
    });
    this.listSectionProperty = listSP?.reduce((result, item) => {
      if(item.entityReferencePropertyName) {
        item.id = `${item.id}|${item.propertyRefId}`;
      }
      const propertyConfig = JSON.parse(
        JSON.stringify(item)
      ) as FilterByPropertyConfigEntity;

      if (
        [
          FilterPropertyType.Address,
          FilterPropertyType['Person Name'],
        ].includes(propertyConfig.type)
      ) {
        let listOption = propertyConfig.optionArr;
        listOption.forEach((option) => {
          const currentOption = JSON.parse(JSON.stringify(option));
          const config = JSON.parse(
            JSON.stringify(item)
          ) as FilterByPropertyConfigEntity;
          if (currentOption.value === 'Prefix') {
            currentOption.value = 'Salutation';
          }
          if (currentOption) {
            config.isRequired = currentOption.isRequired;
          }
          if (currentOption?.key === AddressField.Country) {
            config.options = item.options.filter((el: any) => el.type === LookupTableType.Country);
          } else if (currentOption?.key === AddressField.State) {
            config.options = item.options.filter((el: any) => el.type === LookupTableType.State);
          } else {
            config.options = [currentOption];
          }
          config.propertyTypeLabel = `${
            FilterPropertyType[config.type]
          } - ${currentOption.value}`;
          result.push({
            value: config,
            displayValue: `${config.propertyName} - ${currentOption.value}`,
            valueDescription: !propertyConfig?.entityReferencePropertyId
                            ? (propertyConfig?.configs?.description ? propertyConfig?.configs?.description : '')
                            : (`${propertyConfig?.entityReferencePropertyName + (propertyConfig?.configs?.description ? ' /' + propertyConfig?.configs?.description : '') }`)
          });
        });
        return result;
      }

      propertyConfig.propertyTypeLabel =
        this.FIXED_PROPERTIES[propertyConfig.propertyKey] ||
        FilterPropertyType[propertyConfig.type];
      result.push({
        value: propertyConfig,
          displayValue: `${propertyConfig.propertyName}`,
          valueDescription: !propertyConfig?.entityReferencePropertyId
            ? propertyConfig?.configs?.description
              ? propertyConfig?.configs?.description
              : ''
            : `${propertyConfig?.entityReferencePropertyName + (propertyConfig?.configs?.description ? ' /' + propertyConfig?.configs?.description : '')}`,
      });
      return result;

    }, [] as Option[]).sort((a: any, b: any) => a.displayValue.localeCompare(b.displayValue));
    this.filterByOptions = [...this.listSectionProperty];
  }

  getType(entityPropertyType: EntityPropertyType): string {
    if (
      entityPropertyType === EntityPropertyType.Calculation ||
      entityPropertyType === EntityPropertyType.System ||
      entityPropertyType === EntityPropertyType.Aggregation ||
      entityPropertyType === EntityPropertyType['Entity Reference'] ||
      entityPropertyType === EntityPropertyType.Identifier
    ) {
      return EntityPropertyType[entityPropertyType];
    } else {
      return 'Data';
    }
  }

  onCardSelection(option: Option) {
    this.layoutService.showLoading = true;
    this.cardId = option?.value;
    this.filterFormGroup.setControl('filterBy', this.fb.control(undefined, [Validators.required]));
    if(!this.cardId) {
      this.layoutService.showLoading = false;
      this.listSectionProperty = this.listDefaultData;
      this.mapSectionPropertyData(this.listSectionProperty)
      this.getFilterByOptions();
    }
    else if (this.cardId) {
    this.memberStore.dispatch(MemberListActions.getPropertyByCardSelectionRequest({ cardId: option?.value, screenId: SCREEN_ID.ParticipantList }));
    this.memberStore
      .pipe(
        select(fromMember.selectPropertyByCardSelection),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((res: any) => {
        this.layoutService.showLoading = !!res?.isLoading;
        if (res && !res?.isLoading) {
          const data = deepClone(res.propertyByCardSelection)?.components.filter((item: any) => item?.detailViewColumnType === 0).map((element: any) => element?.entityProfileComponentItems);
          const listProperty = Array.prototype.concat.apply([], data).
                        filter((prop: any) => prop.type !== FilterPropertyType.Aggregation && prop.type !== FilterPropertyType.Calculation);
          this.listSectionProperty = listProperty;
          this.mapSectionPropertyData(listProperty);
          this.getFilterByOptions();
        }
      });
    }
  }

  onFilterBySelect() {
    this.valueOptions = [];
    if (
      this.filterFormGroup.value.filterBy.type ===
      FilterPropertyType.Status
    ) {
      this.handleFilterStatus();
      this.valueOptions = deepClone(this.statusOptions);
      return;
    }
    const filterBy = this.filterFormGroup.value.filterBy;
    this.filterFormGroup = this.initFilterForm(this.filterFormGroup.value);
    this.inputType = INPUT_TYPE[filterBy.type];
    if (filterBy.type === FilterPropertyType.System) {
      if (SystemProperties.includes(filterBy.propertyKey)) {
        this.valueOptions = deepClone(this.booleanOptions);
      } else if (filterBy.propertyKey === AgePropertyName) {
        filterBy.propertyTypeLabel = AgePropertyName;
        this.inputType = INPUT_TYPE[4];
      }
    }
    if (
      [
        'Address - State/Province/Territory',
        'Address - Country',
        'Person Name - Salutation',
      ].includes(filterBy.propertyTypeLabel) ||
      filterBy.isUsedLookupTable ||
      filterBy.type === FilterPropertyType.Boolean ||
      filterBy.type === FilterPropertyType.Department ||
      filterBy.type === FilterPropertyType.Employer
    ) {
      this.inputType = 'Boolean';
    }

    const currentProperty = this.listSectionProperty.find((property) => {
      return (
        property.value.propertyKey === filterBy.propertyKey &&
        property.value.propertyTypeLabel === filterBy.propertyTypeLabel
      );
    }) as any;
    const isRequired = currentProperty?.value?.isRequired;
    const isEncrypted = currentProperty?.value?.isEncryptedProperty;
    const isMultipleLineOption =
      filterBy.type === FilterPropertyType.Department;
    if (filterBy.type === FilterPropertyType.Department) {
      this.isMultipleLineOption = isMultipleLineOption;
    }
    if (filterBy.type === FilterPropertyType.Employer) {
      this.isMultipleLineOption = false;
    }
    if (currentProperty?.value?.options?.length) {
      this.valueOptions = deepClone(
        currentProperty.value.options
      ).map((option: OptionValue) => {
        return {
          displayValue: option.text,
          value: this.getOptionValue(option),
          valueDescription: this.isMultipleLineOption
            ? option.description
            : undefined,
        };
      });
    } else if (
      currentProperty?.value?.type === FilterPropertyType.List &&
      currentProperty?.value?.isUsedLookupTable
    ) {
      this.valueOptions = deepClone(
        currentProperty?.value?.optionValues.length ? currentProperty?.value?.optionValues : []
      ).map((option: OptionValue) => {
        return {
          displayValue: option.text,
          value: option.id,
        };
      });
    }
    const aggregation = filterBy?.configs?.aggregation;
    this.filterOperators = this.getOperatorOptions(
      filterBy.type === FilterPropertyType['Whole Number']
      && aggregation
      && !JSON.parse(JSON.stringify(aggregation))?.aggregationtype ? 'Count' : filterBy.propertyTypeLabel,
      isRequired,
      isEncrypted
    );
    this.inputConfig = this.getInputConfig(
        filterBy.propertyTypeLabel
    );
    this.filterFormGroup.get('operator')?.setValue('');
    this.filterFormGroup.get('valueFrom')?.setValue('');
    this.filterFormGroup.get('valueTo')?.setValue('');
    this.onFilterOperatorSelect();
  }

  getOptionValue(option: OptionValue) {
    switch (option.type) {
      case 4: {
        return true;
      }
      case 5: {
        return false;
      }
      default: {
        return option.id;
      }
    }
  }

  onFilterOperatorSelect() {
    const operator = this.filterFormGroup.value.operator;
    const operatorOptionsStatus = this.getOperatorOptions('StatusEvent');
    const filterBy = this.filterFormGroup.get('filterBy')?.value;
    if (filterBy?.type === FilterPropertyType.Status) {
      if (operator === Operator.NotEquals || operator === Operator.IsAny) {
        this.eventOperatorOptions = operatorOptionsStatus.filter(x => x.value === Operator.IsAny);
      } else {
        this.eventOperatorOptions = operatorOptionsStatus;
      }
      if (operator === Operator.IsAny) {
        this.filterFormGroup = this.fb.group({
          ...this.filterFormGroup.controls,
          valueFrom: [{
            value: '',
            disabled: true
          }],
          valueTo: [{
            value: '',
            disabled: true
          }],
          event: this.fb.group({
            operator: [{
              value: '',
              disabled: false
            }],
            valueFrom: [{
              value: '',
              disabled: true
            }],
          }),
          date: this.fb.group({
            operator: [{
              value: '',
              disabled: true
            }],
            valueFrom: [{
              value: '',
              disabled: true
            }],
            valueTo: [{
              value: '',
              disabled: true
            }],
          }),
        });
      } else {
        this.filterFormGroup = this.fb.group({
          ...this.filterFormGroup.controls,
          valueFrom: this.fb.control('', [Validators.required]),
          valueTo: this.fb.control('', [this.validateBetweenValue()]),
          event: this.fb.group({
            operator: [{
              value: '',
              disabled: true
            }],
            valueFrom: [{
              value: '',
              disabled: true
            }],
          }),
          date: this.fb.group({
            operator: [{
              value: '',
              disabled: true
            }],
            valueFrom: [{
              value: '',
              disabled: true
            }],
            valueTo: [{
              value: '',
              disabled: true
            }],
          }),
        });
      }
      return;
    }

    switch (operator) {
      case Operator.Between: {
        this.placeholder.from = 'Value From';
        this.filterFormGroup.get('valueTo')?.addValidators(Validators.required);
        this.filterFormGroup.get('valueFrom')?.enable();
        break;
      }
      case Operator.IsEmpty:
      case Operator.IsNotEmpty: {
        this.filterFormGroup.get('valueFrom')?.reset('');
        this.filterFormGroup.get('valueFrom')?.disable();
        this.placeholder.from = 'Value';
        this.filterFormGroup
          .get('valueTo')
          ?.removeValidators(Validators.required);
        break;
      }
      default: {
        this.filterFormGroup.get('valueFrom')?.enable();
        this.placeholder.from = 'Value';
        this.filterFormGroup
          .get('valueTo')
          ?.removeValidators(Validators.required);
      }
    }
    this.filterFormGroup.get('valueTo')?.updateValueAndValidity({ emitEvent: false });
  }

  getOperatorOptions(
    filterType: PropertyItem,
    isRequired: boolean = false,
    isEncrypted: boolean = false
  ) {
    return (deepClone(OPERATOR_BY_TYPE[filterType] || []) as Operator[])
      .filter((operator) => {
        return (
          !(
            isRequired &&
            [Operator.IsEmpty, Operator.IsNotEmpty].includes(operator)
          ) &&
          !(
            isEncrypted &&
            filterType !== 'SSN' &&
            ![
              Operator.IsEmpty,
              Operator.IsNotEmpty,
              Operator.Equals,
              Operator.NotEquals,
            ].includes(operator)
          )
        );
      })
      .map((operator) => {
        let displayValue = OPERATOR_LABEL[Operator[operator]];
        if (
          ['DateTime', 'Date'].includes(filterType) &&
          ['GreaterThan', 'LessThan'].includes(Operator[operator])
        ) {
          displayValue = OPERATOR_LABEL['Date' + Operator[operator]];
        }
        return {
          value: operator,
          displayValue,
        };
      });
  }

  addFilter() {
    this.filterFormGroup.markAllAsTouched();
    if (this.filterFormGroup.invalid) {
      return;
    }
    const formValue = this.filterFormGroup.value;
    let propertyType = formValue.filterBy.type;
    const values = [formValue.valueFrom, formValue.valueTo].reduce(
      (result, currentValue) => {
        if (currentValue === '') {
          return result;
        }
        switch (propertyType) {
          case FilterPropertyType.Date: {
            if (currentValue) {
              result.push(
                (currentValue as DateTime)
                  .toUTC(0, { keepLocalTime: true })
                  .toISO()
              );
            }
            break;
          }
          case FilterPropertyType.Status: {
            if (currentValue) {
              result.push(
                this.statusOptions.find(
                  (status) => status.value === currentValue
                )?.displayValue
              );
            }
            break;
          }
          default: {
            result.push(currentValue);
          }
        }
        return result;
      },
      []
    );
    let filterValue: MemberFilterEntity = {
      id: formValue?.filterBy?.entityPropertyId ? formValue?.filterBy?.entityPropertyId : formValue.filterBy.id,
      propertyKey: formValue.filterBy.propertyKey ?? '',
      operator: formValue.operator,
      type: formValue.filterBy.type,
      propertyName: this.getPropertyName(formValue),
      values,
      options: formValue.filterBy.type === FilterPropertyType.List ? formValue.filterBy.options?.text : formValue.filterBy?.options?.[0]?.key,
      selectedPropertyItem: `${formValue.filterBy.propertyName}${
        formValue.filterBy.options?.length &&
        formValue.filterBy.type !== FilterPropertyType.Boolean
          ? ` - ${formValue.filterBy?.options?.[0]?.value}`
          : ''
      }`,
      optionValues: this.getOptionValues(),
      entityId: this.data.entityId,
      propertyRefId: formValue.filterBy.propertyRefId,
      statusNameId: formValue.filterBy.type === FilterPropertyType.Status ? [formValue.valueFrom] : [],
      cardId: this.cardId ?? null
    };
    if (formValue.filterBy.type === FilterPropertyType.Status) {
      filterValue.options = 'Name';
      const event = {
        options: 'Event',
        operator: formValue.event.operator,
        values: [
          this.eventOptions.find(
            (event) => event.value === formValue.event.valueFrom
          )?.displayValue || '',
        ],
        statusEventId: [formValue.event.valueFrom]
      };
      let date;
      if (formValue.date?.operator) {
        date = {
          options: 'Date',
          operator: formValue.date.operator,
          values: [formValue.date.valueFrom],
        };

        if (formValue.date.operator === Operator.IsCurrentStatus) {
          const fromValue = (formValue.date.valueFrom as DateTime)
            .toUTC(0, { keepLocalTime: true })
            .toISO();
          date.values = [fromValue];
        }

        if (formValue.date.operator === Operator.Between) {
          const fromValue = (formValue.date.valueFrom as DateTime)
            .toUTC(0, { keepLocalTime: true })
            .toISO();
          const toValue = (formValue.date.valueTo as DateTime)
            .toUTC(0, { keepLocalTime: true })
            .toISO();
          date.values = [fromValue, toValue];
        }
      }
      filterValue = {
        ...filterValue,
        event,
        date,
      };
      if (date) {
        filterValue = {
          ...filterValue,
          date,
        };
      }
    }
    this.eventOptions = [];
    this.currentFilters = [...this.currentFilters, filterValue];
    this.filterFormGroup = this.initFilterForm();
    this.filterOperators = [];
    this.inputType = 'Text';
    this.placeholder.from = 'Value';
    this.resetForm();
  }

  resetForm() {
    this.filterFormGroup.reset();
    this.listSectionProperty = this.listDefaultData;
    this.mapSectionPropertyData(this.listSectionProperty);
    this.getFilterByOptions();
  }

  private getPropertyName(formValue: any): string{
    return formValue?.filterBy?.propertyName;
  }

  getInputConfig(propertyItem: PropertyItem) {
    switch (propertyItem) {
      case 'Decimal':
        return {
          allowZero: true,
          isDecimal: true,
          isPositive: false,
          suffix: '',
        };
      case 'Currency':
        return {
          allowZero: true,
          isDecimal: true,
          isPositive: false,
          suffix: '$',
        };
      case 'Percentage':
        return {
          allowZero: true,
          isDecimal: true,
          isPositive: true,
          suffix: '%',
        };
      case 'Phone':
      case 'Address - Zip Code/Postal Code':
      case 'Address - Country':
        return {
          allowZero: true,
          isDecimal: false,
          isPositive: true,
          suffix: '',
        };
      case 'Whole Number':
        return {
          allowZero: true,
          isDecimal: false,
          isPositive: false,
          suffix: '',
        };
      default:
        return {
          allowZero: true,
          isDecimal: true,
          isPositive: true,
          suffix: '',
        };
    }
  }

  removeFilterCondition(removedFilter: MemberFilterEntity | EmployerFilter) {
    const removedIndex = this.currentFilters.findIndex(
      (filter) =>
        filter.id === removedFilter.id &&
        filter.options === removedFilter.options
    );
    if (removedIndex > -1) {
      this.currentFilters.splice(removedIndex, 1);
      this.getFilterByOptions();
      this.filterFormGroup.reset('');
    }
  }

  getFilterByOptions() {
    this.filterByOptions = deepClone(this.listSectionProperty).filter(
      (item: any) => {
        return !this.currentFilters.find(
          (filter) =>
            (filter.id === item.value.id || filter.id === item.value.entityPropertyId) &&
            (filter.type === FilterPropertyType.Status ||
              (filter.options &&
                filter.options === item.value.options?.[0]?.key) ||
              !filter.options)
        );
      }
    );
  }

  onCancel() {
    this.dialogRef.close();
  }

  applyFilter() {
    this.dialogRef.close(this.currentFilters);
  }

  validateBetweenValue(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        !control.parent ||
        control.parent.get('operator')?.value !== Operator.Between
      ) {
        return null;
      }
      const { value: valueFrom } = control.parent.get(
        'valueFrom'
      ) as FormControl;
      const { value: valueTo } = control.parent.get('valueTo') as FormControl;
      const isStatusDate =
        control.parent?.parent?.value?.filterBy?.type ===
        FilterPropertyType.Status;
      const isDate =
        control.parent?.get('filterBy')?.value.type ===
        FilterPropertyType.Date;
      const isDateTime =
        control.parent?.get('filterBy')?.value.type ===
        FilterPropertyType.DateTime;
      if (valueFrom && valueTo && (isDate || isDateTime || isStatusDate)) {
        return DateTime.fromISO(valueFrom) >= DateTime.fromISO(valueTo)
          ? {
              fromToError: `The input value must be greater than ${new DatePipe(
                'en-US'
              ).transform(
                valueFrom,
                isDateTime ? 'MM/dd/yyyy hh:mm a' : 'MM/dd/yyyy'
              )}.`,
            }
          : null;
      }
      if (
        valueTo?.length &&
        valueFrom?.length &&
        (+valueFrom > +valueTo || +valueFrom === +valueTo)
      ) {
        return {
          fromToError: `The input value must be greater than ${valueFrom}.`,
        };
      }
      return null;
    };
  }

  updateValidityFormGroup() {
    merge(
      this.filterFormGroup.controls['valueFrom'].valueChanges,
      this.filterFormGroup.controls['valueTo'].valueChanges
    ).subscribe(() => {
      this.filterFormGroup
        .get('valueTo')
        ?.updateValueAndValidity({ emitEvent: false });
    });
  }

  handleFilterStatus() {
    this.filterFormGroup = this.fb.group({
      ...this.filterFormGroup.controls,
      operator: this.fb.control('', [Validators.required]),
      valueFrom: [{
        value: '',
        disabled: true
      }],
      valueTo: [{
        value: '',
        disabled: true
      }],
      event: this.fb.group({
        operator: [{
          value: '',
          disabled: true
        }],
        valueFrom: [{
          value: '',
          disabled: true
        }],
      }),
      date: this.fb.group({
        operator:[{
          value: '',
          disabled: true
        }],
        valueFrom: [{
          value: '',
          disabled: true
        }],
        valueTo: this.fb.control('', [this.validateBetweenValue()]),
      }),
    });
    this.filterOperators = this.getOperatorOptions('StatusName');
    this.inputType = 'Boolean';
  }

  changeStatus() {
    if (
      this.filterFormGroup.value.filterBy.type !==
      FilterPropertyType.Status
    ) {
      return;
    }
    const statusId = this.filterFormGroup.get('valueFrom')?.value;
    this.filterFormGroup = this.fb.group({
      ...this.filterFormGroup.controls,
      event: this.fb.group({
        operator: this.fb.control(''),
        valueFrom:  [{
          value: '',
          disabled: true
        }],
      }),
      date: this.fb.group({
        operator: [{
          value: '',
          disabled: true
        }],
        valueFrom:  [{
          value: '',
          disabled: true
        }],
        valueTo: this.fb.control('', [this.validateBetweenValue()]),
      }),
    });
    this.memberStore.dispatch(
      StatusActions.getStatusDetail({
        query: { SortNames: 'Name', SortType: 0, Id: statusId },
      })
    );
  }

  changeDateOperator() {
    const dateOperator = this.filterFormGroup.value?.date?.operator;
    switch (dateOperator) {
      case Operator.AtAnyPoint: {
        this.filterFormGroup.get('date')?.get('valueFrom')?.disable();
        this.filterFormGroup.get('date')?.get('valueTo')?.disable();
        break;
      }
      case Operator.Between: {
        if (
          this.filterFormGroup.value.date.valueFrom &&
          !DateTime.isDateTime(this.filterFormGroup.value.date.valueFrom)
        ) {
          this.filterFormGroup.get('date')?.get('valueFrom')?.setValue('');
        }
        this.filterFormGroup.get('date')?.get('valueTo')?.enable();
        this.filterFormGroup.get('date')?.get('valueFrom')?.enable();
        this.filterFormGroup
          .get('date')
          ?.get('valueTo')
          ?.addValidators(Validators.required);
        break;
      }
      case Operator.IsCurrentStatus: {
        this.filterFormGroup.get('date')?.get('valueTo')?.disable();
        this.filterFormGroup.get('date')?.get('valueFrom')?.enable();
        if (
          this.filterFormGroup.value.date.valueFrom &&
          !DateTime.isDateTime(this.filterFormGroup.value.date.valueFrom)
        ) {
          this.filterFormGroup.get('date')?.get('valueFrom')?.setValue('');
        }
        break;
      }
      default: {
        this.filterFormGroup.get('date')?.get('valueTo')?.disable();
        this.filterFormGroup.get('date')?.get('valueFrom')?.enable();
        this.filterFormGroup
          .get('date')
          ?.get('valueFrom')
          ?.addValidators(Validators.required);
      }
    }
    (this.filterFormGroup?.controls['date'] as FormGroup)?.controls[
      'valueFrom'
    ]?.updateValueAndValidity();
  }

  initFilterForm(formValue?: any) {
    return this.fb.group({
      cardId: this.fb.control(formValue?.cardId),
      filterBy: this.fb.control(formValue?.filterBy, [Validators.required]),
      operator: this.fb.control('', [Validators.required]),
      valueFrom: this.fb.control('', [Validators.required]),
      valueTo: this.fb.control('', [this.validateBetweenValue()]),
    });
  }

  onEventOperatorSelect() {
    const operatorEventDate = this.getOperatorOptions('StatusDate');
    if (this.filterFormGroup.value?.operator === Operator.IsAny && this.filterFormGroup.value?.event?.operator === Operator.IsAny) {
      this.dateOperatorOptions = operatorEventDate.filter(x => x.value !== Operator.AtAnyPoint);
    } else {
      this.dateOperatorOptions = operatorEventDate;
    }
    if(this.filterFormGroup.value?.event?.operator === Operator.IsAny ){
      this.filterFormGroup = this.fb.group({
        ...this.filterFormGroup.controls,
        event: this.fb.group({
          operator: this.filterFormGroup.value?.event.operator,
          valueFrom: [{
            value: '',
            disabled: true
          }],
        }),
        date: this.fb.group({
          operator: [{
            value: '',
            disabled: false
          }],
          valueFrom:  [{
            value: '',
            disabled: true
          }],
          valueTo: this.fb.control('', [this.validateBetweenValue()]),
        })
      });
    } else {
      this.filterFormGroup = this.fb.group({
        ...this.filterFormGroup.controls,
        event: this.fb.group({
          operator: this.filterFormGroup.value?.event.operator,
          valueFrom: this.fb.control('',[Validators.required]),
        }),
        date: this.fb.group({
          operator: [{
            value: '',
            disabled: true
          }],
          valueFrom:  [{
            value: '',
            disabled: true
          }],
          valueTo: this.fb.control('', [this.validateBetweenValue()]),
        }),
      });

    }
  }

  onEventValueSelect() {
    this.filterFormGroup = this.fb.group({
      ...this.filterFormGroup.controls,
      date: this.fb.group({
        operator: [{
          value: '',
          disabled: false
        }],
        valueFrom:  [{
          value: '',
          disabled: true
        }],
        valueTo: this.fb.control('', [this.validateBetweenValue()]),
      }),
    });
  }

  changeDateValue() {
    (this.filterFormGroup.controls['date'] as FormGroup)?.controls[
      'valueTo'
    ].updateValueAndValidity();
  }

  onValueFromChange(event: any) {
    this.filterFormGroup
      .get('valueTo')
      ?.updateValueAndValidity({ emitEvent: false });
  }

  onChangeFromDate() {
    this.filterFormGroup
      .get('valueTo')
      ?.updateValueAndValidity({ emitEvent: false });
  }

  filterObjBlank(arr: OptionValue[]) {
    return arr?.filter((obj: any) => {
      let keys = Object.keys(obj);
      return keys.some(key => obj[key]);
    });
  }

  getOptionValues() {
    const formValue = this.filterFormGroup.value;
    let optionValues = [];
    if(formValue?.filterBy?.optionValues?.length) {
      optionValues = formValue?.filterBy?.optionValues
    } else if(formValue?.filterBy?.options?.[0]?.optionValues?.length) {
      optionValues = formValue?.filterBy?.options?.[0]?.optionValues
    } else if (formValue?.filterBy?.options?.length && !formValue?.filterBy?.options?.[0]?.optionValues?.length) {
      optionValues = formValue?.filterBy?.options;
    }
    let res = optionValues?.map((optionValue: OptionValue) => {
      return {
        id: optionValue.id,
        text: optionValue.type === LookupTableType.None ? optionValue.description : optionValue.text,
        type: optionValue.type,
      };
    });
    return this.filterObjBlank(res);
  }
}
